import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumber";
import { alpha, useTheme } from "@mui/material/styles";
import { buildMemberDetail, dayOffSuffix } from "../../../shared utils/personDetail";
import { isHoldingGroup } from "../../utils/activityBoardUtils";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import AssignToBusDialog from "../../dialogs/AssignToBusDialog";
import SplitGroupDialog from "../../dialogs/SplitGroupDialog";
import { planWeekendSplit } from "../../utils/weekendGroupSplit";

// Activity products whose groups travel by bus and so can be assigned to a
// journey from the transport column. EVENT (off-site outings) travel too.
const BUS_ELIGIBLE_PRODUCT_TYPES = new Set(["EXTERNAL", "WEEKEND", "EVENT"]);

// Products whose groups need tickets and so can be assigned to a ticket block.
const TICKET_ELIGIBLE_PRODUCT_TYPES = new Set(["EVENT"]);

// How many of the given journeys each person (personType:id) is a passenger on,
// so a member row can show one icon per bus / per ticket group they're assigned to.
function countPassengersByKey(journeysOfType) {
  const counts = new Map();
  (journeysOfType || []).forEach((j) =>
    (Array.isArray(j?.passengers) ? j.passengers : []).forEach((p) => {
      const key = `${String(p?.personType).toUpperCase()}:${p?.id}`;
      counts.set(key, (counts.get(key) || 0) + 1);
    })
  );
  return counts;
}

export default function ActivityGroupCard({
  group,
  leaders = [],
  products = [],
  onEdit,
  onDelete,
  onSaveMembers,
  journeys,
  onAssignMembersToBus,
  onSplitGroup,
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [savingMembers, setSavingMembers] = useState(false);
  const [busDialogOpen, setBusDialogOpen] = useState(false);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [assigningBus, setAssigningBus] = useState(false);
  const [splitDialogOpen, setSplitDialogOpen] = useState(false);

  const theme = useTheme();

  const resolvedLeader = useMemo(() => {
    const fromList = (leaders || []).find(
      (leader) => String(leader.staffId) === String(group?.leaderStaffId)
    );

    if (group?.leaderName) {
      return {
        name: group.leaderName,
        staffId: group.leaderStaffId,
        // Pull the schedule flag from the leaders list so an off-duty leader
        // reads "— day off" even when the group payload carries the name.
        scheduledToday: fromList?.scheduledToday,
      };
    }

    return fromList || null;
  }, [group?.leaderName, group?.leaderStaffId, leaders]);

  const resolvedProduct = useMemo(() => {
    if (group?.productName) {
      return {
        productName: group.productName,
        productId: group.productId,
      };
    }

    return (
      (products || []).find(
        (product) => String(product.productId) === String(group?.productId)
      ) || null
    );
  }, [group?.productName, group?.productId, products]);

  const members = useMemo(() => {
    const list = Array.isArray(group?.members) ? group.members : [];
    return list.slice().sort((a, b) => {
      const aIsAdult = String(a?.personType).toUpperCase() === "ADULT";
      const bIsAdult = String(b?.personType).toUpperCase() === "ADULT";
      if (aIsAdult !== bIsAdult) return aIsAdult ? -1 : 1;
      return String(a?.name || "").localeCompare(String(b?.name || ""));
    });
  }, [group?.members]);

  const counts = useMemo(() => {
    const adults = members.filter((m) => String(m?.personType).toUpperCase() === "ADULT").length;
    const students = members.length - adults;
    return { adults, students, total: members.length };
  }, [members]);

  // Track the *resolved* leader (a staff member actually scheduled to lead that
  // day), not just a configured leaderStaffId — a leader who isn't scheduled is
  // shown as "Unassigned", so the group must flag as needing a leader.
  const hasLeader = Boolean(resolvedLeader?.name);
  const hasMembers = counts.total > 0;
  const capacity = group?.capacity ?? null;
  const isFull = capacity !== null && capacity !== 0 && counts.total >= capacity;

  const statusState = useMemo(() => {
    if (hasLeader && hasMembers) return "ready";
    if ((hasLeader && !hasMembers) || (!hasLeader && hasMembers)) return "partial";
    return "empty";
  }, [hasLeader, hasMembers]);

  const cardSx = useMemo(() => {
    if (statusState === "ready") {
      return {
        borderColor: theme.palette.success.main,
        bgcolor: alpha(theme.palette.success.main, 0.10),
      };
    }

    if (statusState === "partial") {
      return {
        borderColor: theme.palette.warning.main,
        bgcolor: alpha(theme.palette.warning.main, 0.12),
      };
    }

    return {
      borderColor: theme.palette.divider,
      bgcolor: "background.paper",
    };
  }, [statusState, theme]);

  const statusLabel = useMemo(() => {
    if (statusState === "ready") return "Ready";
    if (statusState === "partial") {
      if (hasLeader && !hasMembers) return "Needs members";
      if (!hasLeader && hasMembers) return "Needs leader";
    }
    return "Draft";
  }, [statusState, hasLeader, hasMembers]);

  const statusColor = useMemo(() => {
    if (statusState === "ready") return "success";
    if (statusState === "partial") return "warning";
    return "default";
  }, [statusState]);

  const memberKey = (member) =>
    `${String(member?.personType).toUpperCase()}:${member?.personId}`;

  const handleRequestDelete = () => setConfirmOpen(true);

  const handleConfirmDelete = async () => {
    setConfirmOpen(false);
    await onDelete?.(group);
  };

  const handleRemoveMember = async (memberToRemove) => {
    if (!onSaveMembers || savingMembers) return;

    const removeKey = memberKey(memberToRemove);

    const nextMembers = members
      .filter((member) => memberKey(member) !== removeKey)
      .map((member) => ({
        personType: member.personType,
        personId: member.personId,
        name: member.name,
      }));

    setSavingMembers(true);
    try {
      await onSaveMembers(group.groupId, nextMembers);
    } finally {
      setSavingMembers(false);
    }
  };

  // Only groups that actually travel by bus (external or weekend activities) get
  // the "Add to bus" action. Prefer the group's own productType; otherwise fall
  // back to looking the type up from the products reference list.
  const isBusEligibleProduct = useMemo(() => {
    // Arrivers/Departers holding groups have no product but still travel by bus
    // (notably departers heading to the airport), so they're always bus-eligible.
    if (isHoldingGroup(group)) return true;

    const groupType = String(group?.productType || "").toUpperCase();
    if (groupType) return BUS_ELIGIBLE_PRODUCT_TYPES.has(groupType);

    const linked = (products || []).find(
      (product) => String(product.productId) === String(group?.productId)
    );
    return BUS_ELIGIBLE_PRODUCT_TYPES.has(
      String(linked?.productType || "").toUpperCase()
    );
  }, [group, products]);

  // EVENT groups also need tickets, so they get an "Add to ticket block" action.
  // Unlike buses, holding groups are never ticket-eligible.
  const isTicketEligibleProduct = useMemo(() => {
    const groupType = String(group?.productType || "").toUpperCase();
    if (groupType) return TICKET_ELIGIBLE_PRODUCT_TYPES.has(groupType);

    const linked = (products || []).find(
      (product) => String(product.productId) === String(group?.productId)
    );
    return TICKET_ELIGIBLE_PRODUCT_TYPES.has(
      String(linked?.productType || "").toUpperCase()
    );
  }, [group, products]);

  // A big WEEKEND group with on-duty staff members can be auto-split into one
  // sub-group per staff member (null when not eligible).
  const splitPlan = useMemo(
    () => (onSplitGroup ? planWeekendSplit({ group, products, leaders }) : null),
    [onSplitGroup, group, products, leaders]
  );

  // Split the transport groups: buses vs ticket blocks (journeyType TICKET).
  const busJourneys = useMemo(
    () =>
      (journeys || []).filter(
        (j) => String(j?.journeyType || "").trim().toUpperCase() !== "TICKET"
      ),
    [journeys]
  );
  const ticketJourneys = useMemo(
    () =>
      (journeys || []).filter(
        (j) => String(j?.journeyType || "").trim().toUpperCase() === "TICKET"
      ),
    [journeys]
  );

  // How many buses / ticket groups each member is assigned to, so each row can
  // show one icon per assignment. Keyed personType:id like bus passengers.
  const busCounts = useMemo(() => countPassengersByKey(busJourneys), [busJourneys]);
  const ticketCounts = useMemo(
    () => countPassengersByKey(ticketJourneys),
    [ticketJourneys]
  );

  // The activity leader travels with the group, so include them as an ADULT
  // passenger. The leader is staff (leaderStaffId), but the backend also gives
  // us their adult id (leaderAdultId) which is what buses key passengers on.
  const leaderMember = useMemo(() => {
    if (group?.leaderAdultId == null) return null;
    return {
      personType: "ADULT",
      personId: group.leaderAdultId,
      name: resolvedLeader?.name || group?.leaderName || "",
    };
  }, [group?.leaderAdultId, group?.leaderName, resolvedLeader?.name]);

  // Members plus the leader, de-duped by person (the leader could already be a
  // member, and we never want to send the same person twice).
  const busAssignees = useMemo(() => {
    const seen = new Set();
    return [leaderMember, ...members].filter((person) => {
      if (!person) return false;
      const key = `${String(person.personType).toUpperCase()}:${person.personId}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [leaderMember, members]);

  const canAssignToBus =
    Boolean(onAssignMembersToBus) && busAssignees.length > 0;

  const leaderBusKey = useMemo(
    () =>
      leaderMember
        ? `${String(leaderMember.personType).toUpperCase()}:${leaderMember.personId}`
        : null,
    [leaderMember]
  );

  const handleAssignToBus = async ({ busId, members: chosen }) => {
    if (!onAssignMembersToBus || assigningBus) return;

    setAssigningBus(true);
    try {
      await onAssignMembersToBus({ busId, members: chosen });
      setBusDialogOpen(false);
      setTicketDialogOpen(false);
    } finally {
      setAssigningBus(false);
    }
  };

  const handleClearGroup = async () => {
    if (!onSaveMembers || savingMembers || members.length === 0) return;

    setSavingMembers(true);
    try {
      await onSaveMembers(group.groupId, []);
    } finally {
      setSavingMembers(false);
    }
  };

  const groupTypeLabel = useMemo(() => {
    if (resolvedProduct?.productName) return resolvedProduct.productName;
    return "Bespoke";
  }, [resolvedProduct]);

  return (
    <>
      <Card variant="outlined" sx={{ height: "100%", ...cardSx }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
            <Stack spacing={0.5}>
              <Typography fontWeight={800}>{group.name || "Activity Group"}</Typography>

              <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                <Chip
                  size="small"
                  label={statusLabel}
                  color={statusColor}
                  variant={statusState === "empty" ? "outlined" : "filled"}
                />

                <Chip
                  size="small"
                  label={groupTypeLabel}
                  variant="outlined"
                />

                {isFull && (
                  <Chip size="small" label="Full" color="success" variant="outlined" />
                )}
              </Stack>
            </Stack>

            <Stack direction="row" spacing={0.5} alignItems="center">
              {onEdit && (
                <IconButton size="small" onClick={onEdit} aria-label="Edit activity group">
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
              {onDelete && (
                <IconButton
                  size="small"
                  onClick={handleRequestDelete}
                  aria-label="Delete activity group"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </Stack>
          </Stack>

          <Typography variant="body2">
            <b>Leader:</b> {resolvedLeader?.name || "Unassigned"}
            {dayOffSuffix(resolvedLeader)}
          </Typography>

          <Typography variant="body2">
            <b>Capacity:</b> {counts.total}/{capacity ?? "—"}
          </Typography>

          {group?.notes && (
            <Typography variant="body2" sx={{ opacity: 0.8, mt: 0.5 }}>
              {group.notes}
            </Typography>
          )}

          <Divider sx={{ my: 1 }} />

          <Accordion
            elevation={0}
            disableGutters
            expanded={expanded}
            onChange={(_, isExpanded) => setExpanded(isExpanded)}
            sx={{ "&:before": { display: "none" } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ px: 0, minHeight: 32, "& .MuiAccordionSummary-content": { my: 0 } }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
                <Typography variant="body2" fontWeight={700}>
                  Members
                </Typography>
                <Chip size="small" label={counts.total} />
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  {counts.adults} adults • {counts.students} students
                </Typography>
              </Stack>
            </AccordionSummary>

            <AccordionDetails sx={{ px: 0, pt: 0 }}>
              {expanded && (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                  {/* Holding groups (arrivers/departers) deliberately have no bulk
                      "Clear group" — remove people individually or delete the group. */}
                  {!isHoldingGroup(group) && (
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      disabled={members.length === 0 || savingMembers}
                      onClick={handleClearGroup}
                    >
                      {savingMembers ? "Saving…" : "Clear group"}
                    </Button>
                  )}

                  {onAssignMembersToBus && isBusEligibleProduct && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DirectionsBusIcon fontSize="small" />}
                      disabled={!canAssignToBus || assigningBus}
                      onClick={() => setBusDialogOpen(true)}
                    >
                      {assigningBus ? "Assigning…" : "Add to bus"}
                    </Button>
                  )}

                  {onAssignMembersToBus && isTicketEligibleProduct && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ConfirmationNumberIcon fontSize="small" />}
                      disabled={!canAssignToBus || assigningBus}
                      onClick={() => setTicketDialogOpen(true)}
                    >
                      {assigningBus ? "Assigning…" : "Add to ticket block"}
                    </Button>
                  )}

                  {splitPlan && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CallSplitIcon fontSize="small" />}
                      onClick={() => setSplitDialogOpen(true)}
                    >
                      Split group
                    </Button>
                  )}
                </Stack>
              )}

              {members.length === 0 ? (
                <Typography variant="body2" sx={{ opacity: 0.75 }}>
                  No members assigned yet.
                </Typography>
              ) : (
                <List dense disablePadding sx={{ mt: 0.25 }}>
                  {members.map((member) => {
                    const isAdult = String(member?.personType).toUpperCase() === "ADULT";
                    const name = String(member?.name ?? "").trim();
                    const label =
                      name ||
                      (isAdult
                        ? `Adult #${member?.personId}`
                        : `Student #${member?.personId}`);
                    const detail = buildMemberDetail(member, isAdult);
                    const memberPaxKey = `${String(member?.personType).toUpperCase()}:${member?.personId}`;
                    const busCount = busCounts.get(memberPaxKey) || 0;
                    const ticketCount = ticketCounts.get(memberPaxKey) || 0;

                    return (
                      <ListItem
                        key={`${member.personType}-${member.personId}`}
                        disableGutters
                        sx={{ py: 0, px: 0 }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: "100%" }}>
                          {expanded ? (
                            <IconButton
                              size="small"
                              aria-label="Remove member"
                              onClick={() => handleRemoveMember(member)}
                              color="error"
                              disabled={savingMembers}
                            >
                              <HighlightOffIcon fontSize="small" />
                            </IconButton>
                          ) : (
                            <span style={{ width: 34 }} />
                          )}

                          <Typography variant="body2" sx={{ lineHeight: 1.2, flexGrow: 1, minWidth: 0 }}>
                            <Box component="span" fontWeight={isAdult ? 800 : 400}>
                              {label}
                              {dayOffSuffix(member)}
                            </Box>
                            {detail && (
                              <Box
                                component="span"
                                sx={{ opacity: 0.7 }}
                                title={member.bookingGroupName || undefined}
                              >
                                {` — ${detail}`}
                              </Box>
                            )}
                          </Typography>

                          {(busCount > 0 || ticketCount > 0) && (
                            <Box
                              sx={{
                                ml: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.25,
                                flexShrink: 0,
                              }}
                            >
                              {Array.from({ length: busCount }).map((_, i) => (
                                <DirectionsBusIcon
                                  key={`bus-${i}`}
                                  fontSize="small"
                                  color="success"
                                  titleAccess="Assigned to a bus"
                                />
                              ))}
                              {Array.from({ length: ticketCount }).map((_, i) => (
                                <ConfirmationNumberIcon
                                  key={`ticket-${i}`}
                                  fontSize="small"
                                  color="success"
                                  titleAccess="Assigned to a ticket group"
                                />
                              ))}
                            </Box>
                          )}
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
              )}
            </AccordionDetails>
          </Accordion>
        </CardContent>
      </Card>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete activity group?</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            This will remove the group and unassign its members.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
            {group.name || `Group #${group.groupId}`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleConfirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <AssignToBusDialog
        open={busDialogOpen}
        onClose={() => setBusDialogOpen(false)}
        assignees={busAssignees}
        leaderKey={leaderBusKey}
        journeys={busJourneys}
        assigning={assigningBus}
        onAssign={handleAssignToBus}
      />

      <AssignToBusDialog
        open={ticketDialogOpen}
        onClose={() => setTicketDialogOpen(false)}
        assignees={busAssignees}
        leaderKey={leaderBusKey}
        journeys={ticketJourneys}
        assigning={assigningBus}
        onAssign={handleAssignToBus}
        label="ticket block"
      />

      <SplitGroupDialog
        open={splitDialogOpen}
        onClose={() => setSplitDialogOpen(false)}
        groupName={group?.name}
        plan={splitPlan}
        onConfirm={onSplitGroup}
      />
    </>
  );
}